import { useQuery } from '@apollo/client';
import { IconButton, Stack, Text } from '@fluentui/react';
import clsx from 'clsx';
import { useCommonStyles } from 'common/styles';
import {
  AppRouter,
  TrainingFolderFilter,
  TrainingFoldersOrderBy,
} from 'common/types/globalTypes';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Logo from '../../assets/svgs/logo.svg?react';
import {
  TrainingFolders,
  TrainingFolders_trainingFolders_nodes__childFolders_edges_node__childFolders_nodes_trainingFiles_nodes,
  TrainingFolders_trainingFolders_nodes__childFolders_edges_node_trainingFiles_nodes,
  TrainingFoldersVariables,
} from './__generated__/TrainingFolders';
import FolderNode from './folderNode';
import { HelpSearchBar } from './helpSearchBar';
import { LookupTrainingSearch_lookupTrainingSearch_nodes } from './helpSearchBar/__generated__/LookupTrainingSearch';
import { useStyles } from './index.styles';
import { SearchRightPanel } from './SearchRightPanel';
import TRAINING_DATA from './TrainingFolders.graphql';

interface TrainingSystemModalProps {
  isOpen?: boolean;
}
export const TrainingSystemModal: React.FC<TrainingSystemModalProps> = ({}) => {
  const { topic } = useParams<{ topic?: string }>();
  const [isGlobalSearch, setIsGlobalSearch] = useState<boolean>(false);
  const [isModuleHelpEmpty, setIsModuleHelpEmpty] = useState<boolean>(false);

  const [searchValue, setSearchValue] = useState<string | null>(null);
  const styles = useStyles();
  const commonStyles = useCommonStyles();
  const windowHeight = window.innerHeight;
  const variables: {
    orderBy: TrainingFoldersOrderBy[];
    filter?: TrainingFolderFilter;
  } = {
    orderBy: ['SORT_SEQUENCE_ASC'],
    filter:
      !isGlobalSearch && topic
        ? { appLinkType: { equalTo: topic.toUpperCase() as AppRouter } }
        : { parentId: { isNull: true } },
  };

  const { data: trainingFoldersData, loading: trainingFoldersDataLoading } =
    useQuery<TrainingFolders, TrainingFoldersVariables>(TRAINING_DATA, {
      variables,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-only',
    });
  const [trainingFilesNodes, setTrainingFilesNodes] = useState<
    | TrainingFolders_trainingFolders_nodes__childFolders_edges_node__childFolders_nodes_trainingFiles_nodes[]
    | TrainingFolders_trainingFolders_nodes__childFolders_edges_node_trainingFiles_nodes[]
    | LookupTrainingSearch_lookupTrainingSearch_nodes[]
  >([]);
  const [selectedNodeName, setSelectedNodeName] = useState<string | null>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<string | undefined>(
    undefined
  );
  window.document.title = 'CUT! Expenses Help';

  useEffect(() => {
    if (trainingFoldersDataLoading) return;
    if (!trainingFoldersData?.trainingFolders?.nodes?.length) {
      setIsGlobalSearch(true);
      setIsModuleHelpEmpty(true);
    } else
      setSelectedNodeName(trainingFoldersData?.trainingFolders?.nodes[0]?.name);
    setSelectedFolderId(trainingFoldersData?.trainingFolders?.nodes[0]?.id);
    setTrainingFilesNodes(
      trainingFoldersData?.trainingFolders?.nodes?.[0]?.trainingFiles?.nodes ??
        []
    );
  }, [trainingFoldersData]);

  return (
    <Stack tokens={{ childrenGap: 20 }}>
      <Stack
        horizontal
        className={styles.logoContainer}
        tokens={{ childrenGap: 5, padding: '20px 0px' }}
        verticalAlign="center"
      >
        <IconButton
          onClick={() => window.close()}
          iconProps={{ iconName: 'DoubleChevronLeftMed' }}
          className={styles.iconButtonColor}
          ariaLabel="Collapse"
        />

        <Text
          variant="mediumPlus"
          className={clsx(styles.logoText, commonStyles.semibold)}
        >
          Mastering
        </Text>
        <Logo />
        <Text style={{ paddingLeft: 40, alignSelf: 'center' }}>
          Home
          {topic && !isGlobalSearch && !searchValue
            ? ` / ${
                trainingFoldersData?.trainingFolders?.nodes?.[0]?.name ?? ''
              }`
            : ''}
        </Text>
      </Stack>
      <Stack tokens={{ childrenGap: 30, padding: '0px 40px' }}>
        {searchValue && !!searchValue.trim().length && (
          <Text variant="xLarge" className={commonStyles.colorThemePrimary}>
            {`Searching help for "${searchValue}"`}
          </Text>
        )}
        {!searchValue && (
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Text
              variant="xLargePlus"
              className={commonStyles.colorThemePrimary}
            >
              Knowledge Hub
            </Text>
            <Text
              variant="mediumPlus"
              styles={{ root: { fontStyle: 'italic', marginTop: 6 } }}
            >
              Your on-demand video library
            </Text>
          </Stack>
        )}

        <HelpSearchBar
          isGlobalSearch={isGlobalSearch}
          setIsGlobalSearch={setIsGlobalSearch}
          setSearchValue={setSearchValue}
          searchValue={searchValue}
          setTrainingFilesNodes={setTrainingFilesNodes}
          onModeChange={() => {
            setSelectedFolderId(undefined);
            setSelectedNodeName(null);
          }}
          isModuleHelpEmpty={isModuleHelpEmpty}
          isDataAvailable={
            !!trainingFoldersData?.trainingFolders?.nodes?.length
          }
        />

        <Stack horizontal>
          {!searchValue && (
            <Stack
              styles={{
                root: {
                  height: windowHeight,
                  minWidth: 400,
                },
              }}
            >
              {trainingFoldersData?.trainingFolders && (
                <FolderNode
                  data={trainingFoldersData?.trainingFolders}
                  setTrainingFilesNodes={setTrainingFilesNodes}
                  setSelectedNodeName={setSelectedNodeName}
                  setSelectedFolderId={setSelectedFolderId}
                  selectedFolderId={selectedFolderId}
                />
              )}
            </Stack>
          )}

          <Stack grow tokens={{ childrenGap: 10 }}>
            <SearchRightPanel
              isDataAvailable={
                !!trainingFoldersData?.trainingFolders?.nodes?.length
              }
              trainingFilesNodes={trainingFilesNodes}
              selectedNodeName={searchValue ? searchValue : selectedNodeName}
              trainingFoldersDataLoading={trainingFoldersDataLoading}
            />
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
